import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { albumPhotos, photoUrl, sortPhotos } from '../lib/gallery';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import './AdminDashboard.css';
import './AdminGallery.css';

// Album list and "New album". Admins and super-admins manage albums; viewers can look (row-level security enforces it).
export const canManageGallery = (user) => ['admin', 'super-admin'].includes(user?.role);
export const TITLE_MAX = 120;
export const DESCRIPTION_MAX = 500;
export const CATEGORY_MAX = 40;

const AdminGallery = () => {
  const { user } = useAuth();
  const canEdit = canManageGallery(user);
  const navigate = useNavigate();
  const location = useLocation();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice] = useState(location.state?.message ?? '');
  const [form, setForm] = useState({ title: '', category: '', description: '' });
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: queryError } = await supabase
      .from('gallery_albums')
      .select(`id, title, category, is_published, published_at, sort_order, cover_image_id, updated_at, ${albumPhotos('id, storage_path, alt_text, sort_order, created_at')}`)
      .order('sort_order', { ascending: true })
      .order('updated_at', { ascending: false });
    if (queryError) {
      console.error('Failed to load gallery albums', queryError);
      setError('Failed to load the gallery');
      setAlbums([]);
    } else {
      setAlbums(data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    if (!title) {
      setFormError('Enter a title for the album');
      return;
    }
    setFormError('');
    setCreating(true);
    const { data, error: insertError } = await supabase
      .from('gallery_albums')
      .insert({ title, category: form.category.trim(), description: form.description.trim() })
      .select('id')
      .single();
    setCreating(false);
    if (insertError || !data) {
      console.error('Failed to create album', insertError);
      setFormError(insertError?.code === '42501' ? 'You do not have permission to change the gallery' : 'The album could not be created');
      return;
    }
    navigate(`/admin/gallery/${data.id}`, { state: { message: 'Album created. Add photos, then publish it.' } });
  };

  const published = albums.filter((a) => a.is_published).length;

  return (
    <AdminLayout>
      <div className="admin-dashboard admin-gallery">
        <div className="dashboard-header">
          <div>
            <h1>Gallery</h1>
            <p>Photo albums on the public Gallery page. Only published albums are shown to visitors.</p>
          </div>
        </div>

        {notice && <div className="alert-success" role="status">{notice}</div>}
        {!loading && !error && published === 0 && (
          <div className="info-banner" role="note">
            No album is published yet, so the Gallery page still shows the sample photos. They are replaced as soon as
            you publish your first album.
          </div>
        )}
        {!canEdit && <p className="help-text">You can view albums. Only admins can change the gallery.</p>}

        {canEdit && (
          <form className="gallery-create" onSubmit={create} noValidate>
            <h2>New album</h2>
            <div className="gallery-fields">
              <label htmlFor="album-title">Title</label>
              <input
                id="album-title"
                value={form.title}
                maxLength={TITLE_MAX}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                aria-invalid={Boolean(formError)}
                aria-describedby={formError ? 'album-create-error' : undefined}
              />
              <label htmlFor="album-category">Category (optional)</label>
              <input
                id="album-category"
                value={form.category}
                maxLength={CATEGORY_MAX}
                placeholder="for example Events"
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
              <label htmlFor="album-description">Description (optional)</label>
              <textarea
                id="album-description"
                rows={2}
                value={form.description}
                maxLength={DESCRIPTION_MAX}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            {formError && <p id="album-create-error" className="field-error" role="alert">{formError}</p>}
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create album'}
            </button>
          </form>
        )}

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading albums...</p>
          </div>
        )}
        {error && <div className="error-banner" role="alert">{error}</div>}

        {!loading && !error && (albums.length === 0 ? (
          <p className="no-data">No albums yet.</p>
        ) : (
          <ul className="gallery-album-list">
            {albums.map((album) => {
              const photos = sortPhotos(album.gallery_images);
              const cover = photos.find((p) => p.id === album.cover_image_id) ?? photos[0];
              return (
                <li key={album.id} className="gallery-album-card">
                  <div className="gallery-album-thumb">
                    {cover ? <img src={photoUrl(cover.storage_path)} alt="" loading="lazy" /> : <span>No photos</span>}
                  </div>
                  <div className="gallery-album-info">
                    <h2>{album.title}</h2>
                    <p>
                      <span className={`badge ${album.is_published ? 'badge-published' : 'badge-draft'}`}>
                        {album.is_published ? 'Published' : 'Draft'}
                      </span>{' '}
                      {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
                      {album.category ? ` · ${album.category}` : ''}
                    </p>
                    <Link className="btn btn-secondary" to={`/admin/gallery/${album.id}`}>
                      {canEdit ? 'Manage' : 'View'}<span className="visually-hidden"> {album.title}</span>
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminGallery;
